// AnsprechpartnerFelder — die Zusatzangaben, die es nur beim Ansprechpartner
// eines Produktpartners gibt: Gesellschaft/Zuordnung, Position,
// Funktionsbereich, Spartenbezug und persönliche Zusatzinformationen.
//
// Name, Rufname, Telefon, Mobil, E-Mail, Geburtsdatum und die Privatanschrift
// stehen bewusst NICHT hier — die kommen aus der normalen Privatkontakt-Maske,
// genau wie bei jedem anderen Kontakt.
//
// Die Werte liegen als Strings im Formular-State (Präfix `pp`); der Sparten-
// Mehrfachauswahl-State wird als JSON-Array serialisiert. Bei der Neuanlage
// gehen Kontakt und Profil gemeinsam an das Backend.

import * as React from "react";
import { Button, makeStyles, shorthands } from "@fluentui/react-components";
import {
  FieldRow,
  LabeledInput,
  LabeledSelect,
  SectionCard,
  useFormPrimitiveStyles,
} from "../forms/formPrimitives";
import {
  PP_FUNKTIONSBEREICHE,
  PP_FUNKTIONSBEREICHE_MIT_SPARTEN,
  PP_FUNKTIONSBEREICH_MIT_ANGABE,
  PP_POSITIONEN,
  PP_POSITION_MIT_ANGABE,
  PP_POSITION_MIT_VON,
  PP_SPARTEN,
  spartenLabel,
  PP_SPARTE_SONSTIGES,
  PP_SPARTE_SPEZIALKONZEPTE,
} from "../../data/produktpartnerKatalog";
import { MehrfachSelect } from "../forms/widgets/MehrfachSelect";
import { PP_FIELDS, parseSparten } from "./partnerProfileModel";
import {
  listProduktpartner,
  type ProduktpartnerOption,
} from "../../services/produktpartnerService";

const useStyles = makeStyles({
  hinweis: {
    fontSize: "12px",
    color: "#8a7683",
    ...shorthands.margin("4px", "0", "0"),
  },
  textarea: {
    width: "100%",
    minHeight: "72px",
    fontFamily: "inherit",
    fontSize: "13px",
    ...shorthands.padding("8px", "10px"),
    ...shorthands.border("1px", "solid", "#e2d8de"),
    ...shorthands.borderRadius("8px"),
    resize: "vertical",
  },
});

export type AnsprechpartnerFelderProps = {
  /** Wert eines Formularfeldes. */
  f: (key: string) => string;
  /** Formularfeld setzen. */
  set: (key: string, value: string) => void;
  partnerArt?: "Produktpartner" | "Kooperationspartner";
  nurLesen?: boolean;
  festeGesellschaft?: { id: number; name: string };
};

const Textarea: React.FC<{
  label: string;
  name: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}> = ({ label, name, value, onChange, placeholder }) => {
  const s = useStyles();
  const fs = useFormPrimitiveStyles();
  return (
    <div className={fs.field}>
      <label className={fs.label} htmlFor={name}>
        {label}
      </label>
      <textarea
        id={name}
        name={name}
        autoComplete="off"
        className={s.textarea}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
};

export const AnsprechpartnerFelder: React.FC<AnsprechpartnerFelderProps> = ({
  f,
  set,
  partnerArt = "Produktpartner",
  nurLesen = false,
  festeGesellschaft,
}) => {
  const s = useStyles();
  const [ladefehler, setLadefehler] = React.useState("");
  const [partner, setPartner] = React.useState<ProduktpartnerOption[]>([]);

  React.useEffect(() => {
    const ctrl = new AbortController();
    if (festeGesellschaft) return () => ctrl.abort();
    setLadefehler("");
    listProduktpartner(ctrl.signal, partnerArt)
      .then(setPartner)
      .catch(() => {
        if (!ctrl.signal.aborted)
          setLadefehler("Gesellschaften konnten nicht geladen werden.");
      });
    return () => ctrl.abort();
  }, [partnerArt]);

  const position = f(PP_FIELDS.position);
  const funktionsbereich = f(PP_FIELDS.funktionsbereich);
  const sparten = parseSparten(f(PP_FIELDS.sparten));

  const toggleSparte = (id: string) => {
    if (nurLesen) return;
    const next = sparten.includes(id)
      ? sparten.filter((v) => v !== id)
      : [...sparten, id];
    set(PP_FIELDS.sparten, JSON.stringify(next));
  };

  const zeigeSparten = PP_FUNKTIONSBEREICHE_MIT_SPARTEN.has(funktionsbereich);

  return (
    <fieldset
      disabled={nurLesen}
      style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}
    >
      <SectionCard title="Gesellschaft und Zuordnung">
        <FieldRow columns={1}>
          {festeGesellschaft ? (
            <div>
              <strong>Gesellschaft *</strong>
              <p>{festeGesellschaft.name}</p>
            </div>
          ) : (
            <LabeledSelect
              required
              invalid={!f(PP_FIELDS.partnerContactId)}
              label="Gesellschaft"
              name={PP_FIELDS.partnerContactId}
              value={f(PP_FIELDS.partnerContactId)}
              onChange={(v) => set(PP_FIELDS.partnerContactId, v)}
              options={partner.map((p) => ({
                value: String(p.id),
                label: p.kurzname ? `${p.name} (${p.kurzname})` : p.name,
              }))}
              placeholder={
                partner.length === 0
                  ? `Noch kein ${partnerArt} angelegt`
                  : `${partnerArt} auswählen`
              }
            />
          )}
          {ladefehler && <p role="alert">{ladefehler}</p>}
        </FieldRow>
        <FieldRow columns={2}>
          <LabeledInput
            label="Standort"
            name={PP_FIELDS.standort}
            value={f(PP_FIELDS.standort)}
            onChange={(v) => set(PP_FIELDS.standort, v)}
            placeholder="Direktion Köln"
          />
          <LabeledInput
            label="Organisationseinheit"
            name={PP_FIELDS.organisationseinheit}
            value={f(PP_FIELDS.organisationseinheit)}
            onChange={(v) => set(PP_FIELDS.organisationseinheit, v)}
            placeholder="Maklervertrieb Region West"
          />
        </FieldRow>
      </SectionCard>

      <SectionCard title="Position und Funktion">
        <FieldRow columns={2}>
          <LabeledSelect
            required
            invalid={!position.trim()}
            label="Position"
            name={PP_FIELDS.position}
            value={position}
            onChange={(v) => set(PP_FIELDS.position, v)}
            options={PP_POSITIONEN.map((o) => ({
              value: o.id,
              label: o.label,
            }))}
            placeholder="Bitte auswählen"
          />
          {position === PP_POSITION_MIT_VON && (
            <LabeledInput
              label="Assistenz von"
              name={PP_FIELDS.positionVon}
              value={f(PP_FIELDS.positionVon)}
              onChange={(v) => set(PP_FIELDS.positionVon, v)}
              placeholder="Name der Führungskraft"
            />
          )}
          {position === PP_POSITION_MIT_ANGABE && (
            <LabeledInput
              label="Welche Position?"
              name={PP_FIELDS.positionSonstiges}
              value={f(PP_FIELDS.positionSonstiges)}
              onChange={(v) => set(PP_FIELDS.positionSonstiges, v)}
            />
          )}
        </FieldRow>
        <FieldRow columns={1}>
          <LabeledInput
            label="Freitext zur Position"
            name={PP_FIELDS.positionFreitext}
            value={f(PP_FIELDS.positionFreitext)}
            onChange={(v) => set(PP_FIELDS.positionFreitext, v)}
            placeholder="z. B. Abteilungsname"
          />
        </FieldRow>
        <FieldRow columns={2}>
          <LabeledSelect
            label="Funktionsbereich"
            name={PP_FIELDS.funktionsbereich}
            value={funktionsbereich}
            onChange={(v) => set(PP_FIELDS.funktionsbereich, v)}
            options={PP_FUNKTIONSBEREICHE.map((o) => ({
              value: o.id,
              label: o.label,
            }))}
            placeholder="Bitte auswählen"
          />
          {funktionsbereich === PP_FUNKTIONSBEREICH_MIT_ANGABE && (
            <LabeledInput
              label="Welcher Bereich?"
              name={PP_FIELDS.funktionsbereichSonstiges}
              value={f(PP_FIELDS.funktionsbereichSonstiges)}
              onChange={(v) => set(PP_FIELDS.funktionsbereichSonstiges, v)}
            />
          )}
        </FieldRow>
      </SectionCard>

      {/* Der Spartenbezug ist nur bei den Funktionsbereichen 1–5 fachlich
          sinnvoll — bei Courtage, Recht oder Technik gibt es keine Sparte. */}
      {zeigeSparten && (
        <SectionCard
          title="Spartenbezug / Produktbereich"
          heading="Mehrfachauswahl, optional"
        >
          <MehrfachSelect<string>
            label="Spartenbezug"
            name={PP_FIELDS.sparten}
            value={sparten}
            options={PP_SPARTEN.flatMap((gruppe) =>
              gruppe.optionen.map((o) => ({ value: o.id, label: o.label })),
            )}
            onToggle={toggleSparte}
            beschriftung={
              sparten.length
                ? `${sparten.length} ausgewählt`
                : "Sparten auswählen"
            }
            disabled={nurLesen}
            fertigKnopf
            hinweis="Scrollen Sie in der Liste, um weitere Sparten zu sehen."
          />
          <ul
            aria-label="Ausgewählte Sparten"
            style={{ listStyle: "none", padding: 0 }}
          >
            {sparten.map((id) => (
              <li
                key={id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 12,
                  padding: "4px 0",
                }}
              >
                {spartenLabel(id)}
                <Button
                  type="button"
                  appearance="subtle"
                  disabled={nurLesen}
                  onClick={() => toggleSparte(id)}
                  aria-label={`${spartenLabel(id)} entfernen`}
                >
                  Entfernen
                </Button>
              </li>
            ))}
          </ul>
          {sparten.includes(PP_SPARTE_SPEZIALKONZEPTE) && (
            <FieldRow columns={1}>
              <LabeledInput
                label="Spezialkonzepte — welche?"
                name={PP_FIELDS.spartenSpezialkonzepte}
                value={f(PP_FIELDS.spartenSpezialkonzepte)}
                onChange={(v) => set(PP_FIELDS.spartenSpezialkonzepte, v)}
              />
            </FieldRow>
          )}
          {sparten.includes(PP_SPARTE_SONSTIGES) && (
            <FieldRow columns={1}>
              <LabeledInput
                label="Sonstiges — was?"
                name={PP_FIELDS.spartenSonstiges}
                value={f(PP_FIELDS.spartenSonstiges)}
                onChange={(v) => set(PP_FIELDS.spartenSonstiges, v)}
              />
            </FieldRow>
          )}
        </SectionCard>
      )}

      <SectionCard title="Persönliche Zusatzinformationen">
        <FieldRow columns={2}>
          <LabeledInput
            label="Eintrittsdatum"
            name={PP_FIELDS.eintrittsdatum}
            type="date"
            value={f(PP_FIELDS.eintrittsdatum)}
            onChange={(v) => set(PP_FIELDS.eintrittsdatum, v)}
          />
        </FieldRow>
        <FieldRow columns={2}>
          <LabeledInput
            label="Hobbys"
            name={PP_FIELDS.hobbys}
            value={f(PP_FIELDS.hobbys)}
            onChange={(v) => set(PP_FIELDS.hobbys, v)}
            placeholder="Segeln, Golf"
          />
          <LabeledInput
            label="Vorlieben"
            name={PP_FIELDS.vorlieben}
            value={f(PP_FIELDS.vorlieben)}
            onChange={(v) => set(PP_FIELDS.vorlieben, v)}
            placeholder="Espresso ohne Zucker"
          />
        </FieldRow>
        <Textarea
          label="Bemerkungen"
          name={PP_FIELDS.bemerkungen}
          value={f(PP_FIELDS.bemerkungen)}
          onChange={(v) => set(PP_FIELDS.bemerkungen, v)}
        />
        <div className={s.hinweis}>
          Die Privatanschrift wird oben unter „Anschrift" erfasst.
        </div>
      </SectionCard>
    </fieldset>
  );
};

export default AnsprechpartnerFelder;
